import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { 
  Facebook, Instagram, Twitter, Linkedin, Plus, Trash2, Send, 
  Calendar, Clock, Link2, Image, Edit2, BarChart3, Check, X,
  RefreshCw, AlertCircle, ExternalLink, User, Settings, Share2, Video
} from 'lucide-react';

const PLATFORM_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  linkedin: Linkedin
};

const PLATFORM_COLORS = {
  facebook: '#1877F2',
  instagram: '#E4405F',
  twitter: '#1DA1F2',
  linkedin: '#0A66C2'
};

export default function SocialMediaManager() {
  const { token, user } = useAuth();
  const { currentColors } = useTheme();
  
  const [activeTab, setActiveTab] = useState('accounts');
  const [platforms, setPlatforms] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [postHistory, setPostHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myAds, setMyAds] = useState([]);
  
  // Connect account modal
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectPlatform, setConnectPlatform] = useState('');
  const [demoUsername, setDemoUsername] = useState('');
  const [demoDisplayName, setDemoDisplayName] = useState('');
  const [connecting, setConnecting] = useState(false);
  
  // Create post modal
  const [showPostModal, setShowPostModal] = useState(false);
  const [postContent, setPostContent] = useState('');
  const [postImageUrl, setPostImageUrl] = useState('');
  const [postVideoUrl, setPostVideoUrl] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [creating, setCreating] = useState(false);
  const [showAdsSelector, setShowAdsSelector] = useState(false);
  
  useEffect(() => {
    loadData();
    
    // Check for OAuth callback in URL
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    
    if (code && state) {
      // Extract platform from URL path
      const pathParts = window.location.pathname.split('/');
      const platformIndex = pathParts.indexOf('callback');
      if (platformIndex > -1 && pathParts[platformIndex + 1]) {
        handleOAuthCallback(pathParts[platformIndex + 1], code, state);
      }
    }
  }, []);

  const handleOAuthCallback = async (platform, code, state) => {
    try {
      setConnecting(true);
      const response = await axios.post(
        `${API}/social/oauth/${platform}/callback`,
        { code, state },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        alert(`Successfully connected ${platform}!`);
        loadData();
      }
      
      // Clear URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      console.error('OAuth callback error:', error);
      alert(error.response?.data?.detail || 'Failed to complete OAuth connection');
    } finally {
      setConnecting(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [platformsRes, accountsRes, postsRes, historyRes, analyticsRes, adsRes] = await Promise.all([
        axios.get(`${API}/social/platforms`),
        axios.get(`${API}/social/accounts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/social/posts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/social/history`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/social/analytics`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/marketing/my-ads`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      setPlatforms(platformsRes.data.platforms || []);
      setAccounts(accountsRes.data.accounts || []);
      setScheduledPosts(postsRes.data.posts || []);
      setPostHistory(historyRes.data.history || []);
      setAnalytics(analyticsRes.data);
      setMyAds(adsRes.data || []);
    } catch (error) {
      console.error('Failed to load social media data:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectDemoAccount = async () => {
    if (!demoUsername || !demoDisplayName) {
      alert('Please fill in all fields');
      return;
    }
    
    setConnecting(true);
    try {
      await axios.post(
        `${API}/social/connect/demo`,
        {
          platform: connectPlatform,
          username: demoUsername,
          display_name: demoDisplayName
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowConnectModal(false);
      setDemoUsername('');
      setDemoDisplayName('');
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to connect account');
    } finally {
      setConnecting(false);
    }
  };

  const connectWithOAuth = async (platform) => {
    try {
      setConnecting(true);
      const response = await axios.get(
        `${API}/social/oauth/${platform}/url`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.auth_url) {
        // Redirect to OAuth provider
        window.location.href = response.data.auth_url;
      } else {
        alert('Failed to get OAuth URL');
      }
    } catch (error) {
      console.error('OAuth init error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to initiate OAuth';
      alert(errorMsg);
      setConnecting(false);
    }
  };

  const disconnectAccount = async (accountId) => {
    if (!window.confirm('Are you sure you want to disconnect this account?')) return;
    
    try {
      await axios.delete(`${API}/social/accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadData();
    } catch (error) {
      alert('Failed to disconnect account');
    }
  };

  const createPost = async () => {
    if (!postContent && !postImageUrl && !postVideoUrl) {
      alert('Please add content, an image, or a video');
      return;
    }
    if (selectedAccounts.length === 0) {
      alert('Please select at least one account');
      return;
    }
    
    setCreating(true);
    try {
      let scheduledFor = null;
      if (scheduleDate && scheduleTime) {
        scheduledFor = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
      }
      
      await axios.post(
        `${API}/social/posts`,
        {
          content: postContent,
          image_url: postImageUrl || null,
          video_url: postVideoUrl || null,
          account_ids: selectedAccounts,
          scheduled_for: scheduledFor
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowPostModal(false);
      setPostContent('');
      setPostImageUrl('');
      setPostVideoUrl('');
      setSelectedAccounts([]);
      setScheduleDate('');
      setScheduleTime('');
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setCreating(false);
    }
  };

  const publishPostNow = async (postId) => {
    try {
      const response = await axios.post(
        `${API}/social/posts/${postId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        alert('Post published successfully!');
      } else {
        alert('Some posts failed to publish. Check results for details.');
      }
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to publish post');
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Delete this scheduled post?')) return;
    
    try {
      await axios.delete(`${API}/social/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadData();
    } catch (error) {
      alert('Failed to delete post');
    }
  };

  const toggleAccountSelection = (accountId) => {
    setSelectedAccounts(prev => 
      prev.includes(accountId) 
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  const PlatformIcon = ({ platform, size = 20 }) => {
    const Icon = PLATFORM_ICONS[platform] || Link2;
    return <Icon size={size} style={{ color: PLATFORM_COLORS[platform] || currentColors.primary }} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin" style={{ color: currentColors.primary }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Share2 className="w-6 h-6" style={{ color: currentColors.primary }} />
          <h2 className="text-2xl font-cinzel" style={{ color: currentColors.primary }}>
            Social Media Manager
          </h2>
        </div>
        <button
          onClick={() => setShowPostModal(true)}
          className="cosmic-btn flex items-center gap-2"
          disabled={accounts.length === 0}
        >
          <Plus className="w-5 h-5" />
          <span>Create Post</span>
        </button>
      </div>

      {/* Facebook Connected Notice */}
      {platforms.some(p => p.platform === 'facebook' && p.is_configured) && (
        <div 
          className="p-4 rounded-xl flex items-start gap-4"
          style={{ 
            backgroundColor: 'rgba(34, 197, 94, 0.1)', 
            border: '1px solid rgba(34, 197, 94, 0.3)' 
          }}
        >
          <Check className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-green-400">Facebook/Instagram Integration Active</p>
            <p className="text-sm mt-1" style={{ color: currentColors.textMuted }}>
              Meta API credentials are configured. Click "Connect with Facebook" below to link your Facebook Pages and start posting directly to your audience.
            </p>
          </div>
        </div>
      )}

      {/* API Credentials Notice for unconfigured platforms */}
      {platforms.some(p => !p.is_configured && p.platform !== 'instagram') && (
        <div 
          className="p-4 rounded-xl flex items-start gap-4"
          style={{ 
            backgroundColor: 'rgba(251, 191, 36, 0.1)', 
            border: '1px solid rgba(251, 191, 36, 0.3)' 
          }}
        >
          <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-400">Additional Platforms Available</p>
            <p className="text-sm mt-1" style={{ color: currentColors.textMuted }}>
              To enable Twitter/X and LinkedIn posting, API credentials need to be configured:
            </p>
            <ul className="text-sm mt-2 space-y-1" style={{ color: currentColors.textMuted }}>
              {platforms.filter(p => !p.is_configured && p.platform !== 'instagram').map(p => (
                <li key={p.platform} className="flex items-center gap-2">
                  <PlatformIcon platform={p.platform} size={16} />
                  <span><strong>{p.display_name}:</strong> Requires API credentials</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b" style={{ borderColor: currentColors.border }}>
        {[
          { id: 'accounts', label: 'Connected Accounts', icon: User },
          { id: 'posts', label: 'Scheduled Posts', icon: Calendar },
          { id: 'history', label: 'Post History', icon: Clock },
          { id: 'analytics', label: 'Analytics', icon: BarChart3 }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all ${
              activeTab === tab.id ? 'border-current' : 'border-transparent opacity-60'
            }`}
            style={{ color: activeTab === tab.id ? currentColors.primary : currentColors.textMuted }}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Connected Accounts Tab */}
      {activeTab === 'accounts' && (
        <div className="space-y-6">
          {/* Available Platforms */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-cinzel mb-4" style={{ color: currentColors.text }}>
              Connect New Account
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {platforms.map(platform => (
                <button
                  key={platform.platform}
                  onClick={() => {
                    if (platform.is_configured) {
                      // Use real OAuth for configured platforms
                      connectWithOAuth(platform.platform);
                    } else {
                      // Use demo mode for unconfigured platforms
                      setConnectPlatform(platform.platform);
                      setShowConnectModal(true);
                    }
                  }}
                  disabled={connecting}
                  className="p-4 rounded-xl flex flex-col items-center gap-3 transition-all hover:scale-105 disabled:opacity-50"
                  style={{ 
                    backgroundColor: `${PLATFORM_COLORS[platform.platform]}15`,
                    border: `1px solid ${PLATFORM_COLORS[platform.platform]}30`
                  }}
                >
                  <PlatformIcon platform={platform.platform} size={32} />
                  <span style={{ color: currentColors.text }}>{platform.display_name}</span>
                  {platform.is_configured ? (
                    <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400">
                      Connect Now
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-400">
                      Demo Mode
                    </span>
                  )}
                </button>
              ))}
            </div>
            {connecting && (
              <div className="mt-4 text-center">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto" style={{ color: currentColors.primary }} />
                <p className="text-sm mt-2" style={{ color: currentColors.textMuted }}>Connecting...</p>
              </div>
            )}
          </div>

          {/* Connected Accounts List */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-cinzel mb-4" style={{ color: currentColors.text }}>
              Your Connected Accounts ({accounts.length})
            </h3>
            
            {accounts.length === 0 ? (
              <div className="text-center py-8" style={{ color: currentColors.textMuted }}>
                <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No accounts connected yet</p>
                <p className="text-sm mt-2">Connect your social media accounts to start posting</p>
              </div>
            ) : (
              <div className="space-y-3">
                {accounts.map(account => (
                  <div 
                    key={account.id}
                    className="flex items-center justify-between p-4 rounded-xl"
                    style={{ backgroundColor: `${currentColors.cardBg}` }}
                  >
                    <div className="flex items-center gap-4">
                      {account.profile_image_url ? (
                        <img 
                          src={account.profile_image_url} 
                          alt={account.platform_username}
                          className="w-12 h-12 rounded-full"
                        />
                      ) : (
                        <div 
                          className="w-12 h-12 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${PLATFORM_COLORS[account.platform]}20` }}
                        >
                          <PlatformIcon platform={account.platform} size={24} />
                        </div>
                      )}
                      <div>
                        <p className="font-medium" style={{ color: currentColors.text }}>
                          {account.platform_name}
                        </p>
                        <p className="text-sm" style={{ color: currentColors.textMuted }}>
                          @{account.platform_username}
                        </p>
                      </div>
                      <span 
                        className="px-2 py-1 text-xs rounded-full capitalize"
                        style={{ 
                          backgroundColor: `${PLATFORM_COLORS[account.platform]}20`,
                          color: PLATFORM_COLORS[account.platform]
                        }}
                      >
                        {account.platform}
                      </span>
                    </div>
                    <button
                      onClick={() => disconnectAccount(account.id)}
                      className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-all"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Scheduled Posts Tab */}
      {activeTab === 'posts' && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-cinzel mb-4" style={{ color: currentColors.text }}>
            Scheduled & Draft Posts
          </h3>
          
          {scheduledPosts.length === 0 ? (
            <div className="text-center py-8" style={{ color: currentColors.textMuted }}>
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No scheduled posts</p>
              <button
                onClick={() => setShowPostModal(true)}
                className="mt-4 text-sm underline"
                style={{ color: currentColors.primary }}
              >
                Create your first post
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {scheduledPosts.map(post => (
                <div 
                  key={post.id}
                  className="p-4 rounded-xl"
                  style={{ backgroundColor: `${currentColors.cardBg}` }}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex gap-2">
                      {post.platforms?.map(platform => (
                        <PlatformIcon key={platform} platform={platform} size={18} />
                      ))}
                    </div>
                    <span 
                      className={`px-2 py-1 text-xs rounded-full ${
                        post.status === 'published' ? 'bg-green-500/20 text-green-500' :
                        post.status === 'scheduled' ? 'bg-blue-500/20 text-blue-500' :
                        post.status === 'failed' ? 'bg-red-500/20 text-red-500' :
                        'bg-gray-500/20 text-gray-400'
                      }`}
                    >
                      {post.status}
                    </span>
                  </div>
                  
                  <p className="mb-3 line-clamp-2" style={{ color: currentColors.text }}>
                    {post.content}
                  </p>
                  
                  {post.image_url && (
                    <div className="mb-3">
                      <img 
                        src={post.image_url} 
                        alt="Post preview" 
                        className="h-20 w-auto rounded-lg object-cover"
                      />
                    </div>
                  )}
                  
                  {post.scheduled_for && (
                    <p className="text-sm mb-3" style={{ color: currentColors.textMuted }}>
                      <Clock className="w-4 h-4 inline mr-1" />
                      Scheduled: {new Date(post.scheduled_for).toLocaleString()}
                    </p>
                  )}
                  
                  {post.status !== 'published' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => publishPostNow(post.id)}
                        className="px-3 py-1.5 text-sm rounded-lg flex items-center gap-1 transition-all"
                        style={{ 
                          backgroundColor: `${currentColors.primary}20`,
                          color: currentColors.primary
                        }}
                      >
                        <Send className="w-4 h-4" />
                        Publish Now
                      </button>
                      <button
                        onClick={() => deletePost(post.id)}
                        className="px-3 py-1.5 text-sm rounded-lg text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Post History Tab */}
      {activeTab === 'history' && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-cinzel mb-4" style={{ color: currentColors.text }}>
            Post History
          </h3>
          
          {postHistory.length === 0 ? (
            <div className="text-center py-8" style={{ color: currentColors.textMuted }}>
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No posts published yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {postHistory.map(item => (
                <div 
                  key={item.id}
                  className="flex items-center justify-between p-4 rounded-xl"
                  style={{ backgroundColor: `${currentColors.cardBg}` }}
                >
                  <div className="flex items-center gap-4">
                    <PlatformIcon platform={item.platform} size={24} />
                    <div>
                      <p className="line-clamp-1" style={{ color: currentColors.text }}>
                        {item.content?.substring(0, 60)}...
                      </p>
                      <p className="text-sm" style={{ color: currentColors.textMuted }}>
                        {new Date(item.posted_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <span 
                    className={`px-2 py-1 text-xs rounded-full ${
                      item.status === 'published' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                    }`}
                  >
                    {item.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="glass-card p-6 text-center">
            <p className="text-3xl font-bold" style={{ color: currentColors.primary }}>
              {analytics.total_posts || 0}
            </p>
            <p className="text-sm mt-1" style={{ color: currentColors.textMuted }}>Total Posts</p>
          </div>
          <div className="glass-card p-6 text-center">
            <p className="text-3xl font-bold" style={{ color: currentColors.secondary }}>
              {analytics.connected_accounts || 0}
            </p>
            <p className="text-sm mt-1" style={{ color: currentColors.textMuted }}>Connected Accounts</p>
          </div>
          <div className="glass-card p-6 text-center">
            <p className="text-3xl font-bold" style={{ color: currentColors.accent }}>
              {analytics.scheduled_pending || 0}
            </p>
            <p className="text-sm mt-1" style={{ color: currentColors.textMuted }}>Scheduled Posts</p>
          </div>
          <div className="glass-card p-6 text-center">
            <div className="flex justify-center gap-3">
              {Object.entries(analytics.posts_by_platform || {}).map(([platform, count]) => (
                <div key={platform} className="text-center">
                  <PlatformIcon platform={platform} size={20} />
                  <p className="text-lg font-bold mt-1" style={{ color: currentColors.text }}>{count}</p>
                </div>
              ))}
            </div>
            <p className="text-sm mt-2" style={{ color: currentColors.textMuted }}>By Platform</p>
          </div>
        </div>
      )}

      {/* Connect Account Modal */}
      {showConnectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div 
            className="w-full max-w-md rounded-2xl p-6"
            style={{ backgroundColor: currentColors.background }}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-cinzel flex items-center gap-2" style={{ color: currentColors.text }}>
                <PlatformIcon platform={connectPlatform} size={24} />
                Connect {connectPlatform.charAt(0).toUpperCase() + connectPlatform.slice(1)}
              </h3>
              <button onClick={() => setShowConnectModal(false)}>
                <X className="w-6 h-6" style={{ color: currentColors.textMuted }} />
              </button>
            </div>
            
            <div className="p-4 rounded-xl mb-4" style={{ backgroundColor: 'rgba(251, 191, 36, 0.1)' }}>
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 mt-0.5 text-amber-400" />
                <div>
                  <p className="text-sm font-medium text-amber-400">OAuth Integration Pending</p>
                  <p className="text-xs mt-1" style={{ color: currentColors.textMuted }}>
                    Real OAuth integration requires API credentials from the platform administrator. This form saves a placeholder account for testing the UI workflow.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Username (without @)
                </label>
                <input
                  type="text"
                  value={demoUsername}
                  onChange={(e) => setDemoUsername(e.target.value)}
                  placeholder="johndoe"
                  className="cosmic-input w-full"
                />
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Display Name
                </label>
                <input
                  type="text"
                  value={demoDisplayName}
                  onChange={(e) => setDemoDisplayName(e.target.value)}
                  placeholder="John Doe"
                  className="cosmic-input w-full"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowConnectModal(false)}
                className="flex-1 px-4 py-2 rounded-xl"
                style={{ 
                  backgroundColor: `${currentColors.textMuted}20`,
                  color: currentColors.textMuted
                }}
              >
                Cancel
              </button>
              <button
                onClick={connectDemoAccount}
                disabled={connecting}
                className="flex-1 cosmic-btn"
              >
                {connecting ? 'Connecting...' : 'Connect Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Post Modal */}
      {showPostModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div 
            className="w-full max-w-lg rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: currentColors.background }}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-cinzel" style={{ color: currentColors.text }}>
                Create New Post
              </h3>
              <button onClick={() => setShowPostModal(false)}>
                <X className="w-6 h-6" style={{ color: currentColors.textMuted }} />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Select Accounts */}
              <div>
                <label className="block text-sm mb-2" style={{ color: currentColors.textMuted }}>
                  Post to
                </label>
                <div className="flex flex-wrap gap-2">
                  {accounts.map(account => (
                    <button
                      key={account.id}
                      onClick={() => toggleAccountSelection(account.id)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all ${
                        selectedAccounts.includes(account.id) ? 'ring-2' : ''
                      }`}
                      style={{ 
                        backgroundColor: selectedAccounts.includes(account.id) 
                          ? `${PLATFORM_COLORS[account.platform]}20` 
                          : `${currentColors.textMuted}10`,
                        ringColor: PLATFORM_COLORS[account.platform]
                      }}
                    >
                      <PlatformIcon platform={account.platform} size={16} />
                      <span className="text-sm" style={{ color: currentColors.text }}>
                        @{account.platform_username}
                      </span>
                      {selectedAccounts.includes(account.id) && (
                        <Check className="w-4 h-4 text-green-500" />
                      )}
                    </button>
                  ))}
                </div>
                {accounts.length === 0 && (
                  <p className="text-sm mt-2" style={{ color: currentColors.textMuted }}>
                    Connect social accounts first to create posts
                  </p>
                )}
              </div>
              
              {/* Content */}
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Post Content
                </label>
                <textarea
                  value={postContent}
                  onChange={(e) => setPostContent(e.target.value)}
                  rows="4"
                  placeholder="What's on your mind?"
                  className="cosmic-input w-full"
                  maxLength={2200}
                />
                <p className="text-xs text-right mt-1" style={{ color: currentColors.textMuted }}>
                  {postContent.length}/2200
                </p>
              </div>
              
              {/* Image URL */}
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Image URL (optional)
                </label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={postImageUrl}
                    onChange={(e) => {
                      setPostImageUrl(e.target.value);
                      if (e.target.value) setPostVideoUrl(''); // Clear video if image is set
                    }}
                    placeholder="https://..."
                    className="cosmic-input flex-1"
                    disabled={!!postVideoUrl}
                  />
                  <button
                    onClick={() => setShowAdsSelector(!showAdsSelector)}
                    className="px-3 py-2 rounded-xl"
                    style={{ backgroundColor: `${currentColors.secondary}20`, color: currentColors.secondary }}
                    title="Select from Marketing Ads"
                  >
                    <Image className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Ads Selector */}
                {showAdsSelector && myAds.length > 0 && (
                  <div 
                    className="mt-2 p-3 rounded-xl max-h-48 overflow-y-auto"
                    style={{ backgroundColor: `${currentColors.background}50`, border: `1px solid ${currentColors.border}` }}
                  >
                    <p className="text-xs mb-2 font-medium" style={{ color: currentColors.textMuted }}>
                      Select from your Marketing Ads:
                    </p>
                    <div className="grid grid-cols-4 gap-2">
                      {myAds.map(ad => (
                        <button
                          key={ad.id}
                          onClick={() => {
                            const imageUrl = `${window.location.origin}${ad.image_url}`;
                            setPostImageUrl(imageUrl);
                            setPostVideoUrl('');
                            setShowAdsSelector(false);
                            if (!postContent) {
                              setPostContent(ad.headline + (ad.subtitle ? '\n\n' + ad.subtitle : ''));
                            }
                          }}
                          className="rounded-lg overflow-hidden hover:ring-2 transition-all"
                          style={{ ringColor: currentColors.primary }}
                        >
                          <img 
                            src={`${API.replace('/api', '')}${ad.image_url}`}
                            alt={ad.headline}
                            className="w-full h-16 object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {showAdsSelector && myAds.length === 0 && (
                  <p className="text-xs mt-2" style={{ color: currentColors.textMuted }}>
                    No ads created yet. Go to Marketing Tools to create ads.
                  </p>
                )}
                
                {postImageUrl && (
                  <img 
                    src={postImageUrl} 
                    alt="Preview" 
                    className="mt-2 h-24 w-auto rounded-lg object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
              </div>

              {/* Video URL */}
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Video URL (optional)
                </label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={postVideoUrl}
                    onChange={(e) => {
                      setPostVideoUrl(e.target.value);
                      if (e.target.value) setPostImageUrl(''); // Clear image if video is set
                    }}
                    placeholder="https://..."
                    className="cosmic-input flex-1"
                    disabled={!!postImageUrl}
                  />
                  <button
                    className="px-3 py-2 rounded-xl"
                    style={{ backgroundColor: `${currentColors.secondary}20`, color: currentColors.secondary }}
                  >
                    <Video className="w-5 h-5" />
                  </button>
                </div>
                {postVideoUrl && (
                  <p className="text-xs mt-1 text-green-400">Video URL set - will be uploaded to Facebook</p>
                )}
                <p className="text-xs mt-1" style={{ color: currentColors.textMuted }}>
                  Note: Either image OR video can be attached, not both
                </p>
              </div>
              
              {/* Schedule */}
              <div>
                <label className="block text-sm mb-2" style={{ color: currentColors.textMuted }}>
                  Schedule (optional)
                </label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="cosmic-input flex-1"
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    className="cosmic-input flex-1"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowPostModal(false)}
                className="flex-1 px-4 py-2 rounded-xl"
                style={{ 
                  backgroundColor: `${currentColors.textMuted}20`,
                  color: currentColors.textMuted
                }}
              >
                Cancel
              </button>
              <button
                onClick={createPost}
                disabled={creating || selectedAccounts.length === 0}
                className="flex-1 cosmic-btn flex items-center justify-center gap-2"
              >
                {creating ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {scheduleDate ? <Calendar className="w-5 h-5" /> : <Send className="w-5 h-5" />}
                    <span>{scheduleDate ? 'Schedule Post' : 'Create Post'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
